import { Link } from "react-router-dom";
import { BLOG_POSTS } from "../../../data/blogPosts";
import AnimatedBackground from "../../common/AnimatedBackground";
import SEOHead from "../../common/SEOHead";
import { BiTime, BiCalendar, BiSearch, BiX } from "react-icons/bi";
import { useEffect, useState, useMemo } from "react";
import Footer from "../Footer/Footer";
import AdUnit from "../../common/AdUnit";

function formatDate(dateStr: string): string {
  const date = new Date(dateStr + "T00:00:00");
  return date.toLocaleDateString("es-AR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export default function BlogList() {
  const [query, setQuery] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return BLOG_POSTS;
    return BLOG_POSTS.filter(
      (p) =>
        p.title.toLowerCase().includes(q) ||
        p.summary.toLowerCase().includes(q) ||
        p.tags.some((t) => t.toLowerCase().includes(q))
    );
  }, [query]);

  return (
    <div className="relative w-full min-h-lvh text-white">
      <SEOHead
        title="Blog"
        description="Artículos técnicos sobre desarrollo web, automatización, Python, Arduino y más. Por Franco Martino."
        url="/blog"
      />
      <AnimatedBackground />

      <div className="relative max-w-4xl mx-auto px-6 sm:px-12 py-10">
        {/* Page header */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold font-header text-white">
            Blog
          </h1>
          <p className="text-gray-500 mt-1 text-sm">
            {BLOG_POSTS.length} artículo{BLOG_POSTS.length !== 1 ? "s" : ""}
          </p>
        </div>

        {/* Search bar */}
        <div className="relative mb-8">
          <BiSearch
            size={16}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por título, resumen o tag..."
            className="w-full pl-9 pr-10 py-2.5 rounded-xl bg-gray-800/60 border border-gray-700/50
              text-white placeholder-gray-600 text-sm
              focus:outline-none focus:border-green-300/50 focus:bg-gray-800
              transition-all duration-200"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
              aria-label="Limpiar búsqueda"
            >
              <BiX size={16} />
            </button>
          )}
        </div>

        <div className="h-px bg-gray-700/60 mb-8" />

        {/* Results count when filtering */}
        {query && (
          <p className="text-xs text-gray-500 mb-4">
            {filtered.length === 0
              ? "Sin resultados para "
              : `${filtered.length} resultado${filtered.length !== 1 ? "s" : ""} para `}
            <span className="text-gray-300">"{query}"</span>
          </p>
        )}

        {/* Posts list */}
        <div className="flex flex-col gap-4">
          {filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-600">
              <BiSearch size={32} className="mx-auto mb-3 opacity-40" />
              <p>No se encontraron artículos.</p>
            </div>
          ) : (
            filtered.map((post, index) => (
              <div key={post.slug}>
                <Link
                  to={`/blog/${post.slug}`}
                  className="group flex flex-col sm:flex-row gap-0 rounded-xl bg-gray-800/60 border border-gray-700/40 hover:border-gray-600/70 overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-black/20"
                >
                  {/* Cover image */}
                  {post.coverImage && (
                    <div className="sm:w-52 shrink-0 overflow-hidden">
                      <img
                        src={post.coverImage.startsWith("/") || post.coverImage.startsWith("http") ? post.coverImage : `/${post.coverImage}`}
                        alt={`Portada: ${post.title}`}
                        className="w-full h-full object-cover aspect-[3/2] sm:aspect-auto transition-transform duration-500 group-hover:scale-[1.03]"
                      />
                    </div>
                  )}

                  {/* Content */}
                  <div className="flex flex-col gap-2.5 p-5 flex-1">
                    {/* Meta */}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <BiCalendar size={13} />
                        {formatDate(post.date)}
                      </span>
                      <span className="flex items-center gap-1">
                        <BiTime size={13} />
                        {post.readingTime} min de lectura
                      </span>
                    </div>

                    {/* Title */}
                    <h2 className="text-white font-semibold text-base leading-snug group-hover:text-green-300 transition-colors duration-200">
                      {post.title}
                    </h2>

                    {/* Summary */}
                    <p className="text-gray-400 text-sm leading-relaxed line-clamp-2 flex-1">
                      {post.summary}
                    </p>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {post.tags.slice(0, 4).map((tag) => (
                        <span
                          key={tag}
                          className="text-xs bg-gray-700/60 text-gray-400 px-2 py-0.5 rounded-md border border-gray-700/40"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
                {/* Anuncio después del segundo post */}
                {index === 1 && (
                  <AdUnit
                    slot="6805590889"
                    format="horizontal"
                    label="Publicidad"
                    className="my-4"
                  />
                )}
              </div>
            ))
          )}
        </div>

        {/* Back link */}
        <div className="mt-10 flex justify-center">
          <Link
            to="/"
            className="text-gray-500 hover:text-white text-sm transition-colors duration-200 underline underline-offset-4"
          >
            ← Volver al portfolio
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
}
